import React from 'react'

import {
  Tooltip,
  TruncateWithTooltip,
  TimeAgo,
  useResponsiveDropdown,
} from '@sketch/components'

import {
  Wrapper,
  Description,
  TopRow,
  Author,
  Text,
  VersionOptionsButton,
  StarWrapper,
  DateTime,
  ToolTipAuthor,
  Order,
} from './VersionItem.styles'

import { ReactComponent as DotIcon } from '@sketch/icons/dots-3-horizontal-16'

// Usage of RenderStatusType will not break the build
// eslint-disable-next-line no-restricted-imports
import { RenderStatusType } from '@sketch/gql-types/expansive'

export interface VersionItemProps {
  className?: string
  description?: string
  authorName: string
  creationDate: string
  renderStatus?: RenderStatusType
  deleted?: boolean
  optionsMenu?: () => React.ReactNode
  star?: (() => React.ReactNode) | false
  starred?: boolean
  order?: number
}

const RENDER_STATUS_COPY = {
  CREATED: 'Uploading document...',
  UPLOADING: 'Uploading document...',
  UPLOADED: 'Processing document...',
  RENDERING: 'Processing document...',
  PROCESSING: 'Processing document...',
  FAILED: 'Processing failed...',
  ERRORED: 'Processing failed...',
  LOADING: null,
} as const

const options = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
} as const
const dateFormatter = new Intl.DateTimeFormat('default', options)

const VersionItem = (props: VersionItemProps) => {
  const {
    className,
    description,
    authorName,
    creationDate,
    deleted,
    optionsMenu,
    star,
    renderStatus,
    starred,
    order,
  } = props

  const dateFormat = dateFormatter.format(new Date(creationDate)).split(', ')
  const versionFinishedRendering = renderStatus === 'FINISHED'

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: function Dropdown() {
      return <>{optionsMenu?.()}</>
    },
    placement: 'bottom-end',
    usePortal: true,
  })

  let metaText
  if (!renderStatus || renderStatus === 'FINISHED') {
    metaText = (
      <Tooltip
        content={
          <>
            {dateFormat[0]}
            <br />
            <DateTime>{dateFormat[1]}</DateTime>
          </>
        }
        placement="bottom-start"
      >
        {props => (
          <span {...props}>
            <TimeAgo date={creationDate} short hideTitle />
          </span>
        )}
      </Tooltip>
    )
  } else {
    metaText = RENDER_STATUS_COPY[renderStatus]
  }

  return (
    <Wrapper
      className={className}
      data-testid="version-item"
      $disabled={deleted || !versionFinishedRendering}
      $starred={starred}
    >
      <TopRow>
        {order && <Order>#{order}</Order>}

        {star && <StarWrapper>{star()}</StarWrapper>}

        <Author>
          <TruncateWithTooltip placement="bottom-end">
            <ToolTipAuthor>{authorName}</ToolTipAuthor>
          </TruncateWithTooltip>
        </Author>

        <Text>{metaText}</Text>

        {deleted && <Text>(Deleted)</Text>}

        {optionsMenu && (
          <>
            <VersionOptionsButton
              data-testid="version-item-options-button"
              {...buttonProps}
            >
              <DotIcon role="img" />
              <span className="sr-only">Comment options</span>
            </VersionOptionsButton>
            {content}
          </>
        )}
      </TopRow>

      {description && (
        <Description data-testid="version-item-description">
          {description}
        </Description>
      )}
    </Wrapper>
  )
}

export default VersionItem
